import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { BedChoice, EgsRow } from '../tabs/egs/previousEgs';
import type { RoomAssetLink } from '../tabs/packageOptions/types';

import CopyEgsRow, {
  type EgsBedChange,
  type EgsOtherBedChange,
  type EgsRoomsChange,
  type EgsSkipChange,
} from './CopyEgsRow';

type CopyEgsStepProps = {
  bedByNameKey: Record<string, BedChoice>;
  nothingToCopy: boolean;
  onBedChange: EgsBedChange;
  onOtherBedChange: EgsOtherBedChange;
  onRoomsChange: EgsRoomsChange;
  onSkipChange: EgsSkipChange;
  otherBedByNameKey: Record<string, string>;
  rooms: Array<RoomAssetLink>;
  rows: Array<EgsRow>;
  roomsByNameKey: Record<string, Array<string>>;
  skippedRows: ReadonlySet<string>;
};

export default function CopyEgsStep({
  bedByNameKey,
  nothingToCopy,
  onBedChange,
  onOtherBedChange,
  onRoomsChange,
  onSkipChange,
  otherBedByNameKey,
  rooms,
  rows,
  roomsByNameKey,
  skippedRows,
}: CopyEgsStepProps) {
  return (
    <>
      <Typography sx={{ mt: 2 }} variant="subtitle2">
        Rooms and extra bed for each extra guest surcharge
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Rooms are read from the Salesforce names: check them, and pick the rooms where none matched.
        {rows.some((row) => row.bedToChoose) &&
          ' Where Salesforce only says "Roll-away, sofa bed or daybed", pick the extra bed.'}{' '}
        Skip one whose room this Opportunity doesn&apos;t have: it won&apos;t be copied.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Salesforce name</TableCell>
            <TableCell>Guests</TableCell>
            <TableCell>Rooms</TableCell>
            <TableCell>Extra bed</TableCell>
            <TableCell>Skip</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <CopyEgsRow
              allRooms={rooms}
              bed={bedByNameKey[row.key]}
              key={row.key}
              onBedChange={onBedChange}
              onOtherBedChange={onOtherBedChange}
              onRoomsChange={onRoomsChange}
              onSkipChange={onSkipChange}
              otherBed={otherBedByNameKey[row.key] ?? ''}
              row={row}
              rooms={roomsByNameKey[row.key]}
              skipped={skippedRows.has(row.key)}
            />
          ))}
        </TableBody>
      </Table>
      {nothingToCopy && (
        <Typography color="error" sx={{ mt: 1 }} variant="body2">
          Every extra guest surcharge is skipped. Untick Extra Guest Surcharges on What to copy instead.
        </Typography>
      )}
    </>
  );
}
